import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    Button, Dialog, DialogContent,
    DialogActions, makeStyles,
    Checkbox, FormControlLabel,
    MenuItem, ListItemText, Slider,
    Tooltip,
} from '@material-ui/core';
import { Form, Col } from 'react-bootstrap';
import {
    useQuery,
    useLazyQuery,
    useMutation,
} from '@apollo/client';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import { FetchPolicy, AI_PLATFORM } from 'utils/enum/Core';
import { NOISE_REDUCTION, VOICE } from 'utils/enum/OpenAIEnum';
import CoreSelect from '@material-ui/core/Select';
import ModalUtils from 'utils/ModalUtils';
import StringUtils from 'lib/StringUtils';
import ButtonStyles from 'styles/theme/Button';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import Select from 'components/widgets/Select';
import AssistantFiles from 'components/modules/settings/ai/AssistantFiles';
import AssistantFunctions from 'components/modules/settings/ai/AssistantFunctions';
import AIQuery from 'services/graphQL/query/ai/AIQuery';
import AIMutation from 'services/graphQL/mutate/ai/AIMutation';
import UserQuery from 'services/graphQL/query/UserQuery';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    AppBar: {
        color: theme.palette.text.white,
        backgroundColor: theme.palette.background.sanMarino,
        '& h4': {
            color: theme.palette.text.white,
            fontSize: '15px',
        },
    },
    content: {
        padding: '20px 10px',
        [theme.breakpoints.down('sm')]: {
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
    aiGeneralSettings: {
        display: 'flex',
        fontSize: '13px',
        '& > div': {
            width: '50%',
        },
    },
    aiInstructions: {
        fontSize: '13px',
        marginTop: '15px',
        '& textarea': {
            resize: 'none',
            height: '400px',
        },
    },
    aiFilesSection: {
        marginTop: '15px',
        fontSize: '13px',
        '& > div > div': {
            display: 'flex',
            marginBottom: '2px',
            justifyContent: 'space-between',
            '& > div:nth-child(1)': {
                width: '50%',
                display: 'flex',
                '& > input': {
                    width: '250px',
                    marginRight: '5px',
                },
            },
            '& > div:nth-child(2)': {
                width: '50%',
                display: 'flex',
                justifyContent: 'flex-end',
                '& > div': {
                    width: '250px',
                    marginRight: '5px',
                },
            },
        },
    },
    aiFunctionsSection: {
        marginTop: '15px',
        fontSize: '13px',
        '& > div > div:nth-child(2)': {
            display: 'flex',
            justifyContent: 'flex-start',
            marginBottom: '2px',
            '& > div': {
                width: '250px',
                marginRight: '5px',
                '& > div': {
                    height: '34px',
                },
            },
            '& > input': {
                width: '250px',
                marginRight: '5px',
            },
        },
    },
    input: {
        fontSize: '13px',
        resize: 'none',
        width: '100%',
    },
    '@global': {
        '.css-26l3qy-menu div': {
            fontSize: '13px',
            lineHeight: '1.4',
        },
    },
    select: {
        width: '100%',
        border: `1px solid ${theme.palette.border.ghost}`,
        borderRadius: '0.25rem',
        height: '34px',
        fontSize: '13px',
        '& > div': {
            paddingLeft: '10px !important',
            paddingRight: '10px !important',
        },
    },
    voiceSection: {
        marginTop: '10px',
        marginBottom: '10px',
        '& > div:nth-child(1)': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            '& span': {
                fontSize: '13px',
            },
        },
    },
    voiceSettings: {
        display: 'flex',
        flexDirection: 'row',
        fontSize: '13px',
        '& > div:nth-child(1)': {
            width: '60%',
        },
        '& > div:nth-child(2)': {
            width: '40%',
        },
    },
    slider: {
        '& span.MuiSlider-valueLabel > span': {
            width: '35px',
            height: '35px',
        },
    },
}));

const MenuProps = {
    anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
    },
    transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
    },
    PaperProps: {
        style: {
            maxHeight: 200,
            width: 250,
        },
    },
    getContentAnchorEl: null,
};

const AssistantForm = ({ assistant, toggleDialog }) => {
    const keyStore = new KeyStore();
    const AI_GLOBAL_SETTINGS_READ = keyStore.hasPermission(Permission.AI_GLOBAL_SETTINGS_READ);

    const classes = { ...useStyles(), ...buttonStyles() };
    const filesReference = useRef();
    const functionsReference = useRef();

    const currentAssistant = assistant ?? {};
    const {
        aiAssistantId,
        companyCode,
        instructions,
        isLive: isAssistantLive,
        notifyAIWhilePaused: isAINotificationOn,
        maxResponseTokens,
        model,
        name: assistantName,
        temperature,
        files,
        functions: assistantFunctions,
        analysisEmployeeTypes,
    } = currentAssistant;

    const [state, setState] = useState({
        companies: [],
        models: [],
        isLive: isAssistantLive ?? false,
        notifyAIWhilePaused: isAINotificationOn ?? false,
        selectedName: assistantName,
        selectedTemperature: temperature ?? 0.8,
        selectedMaxTokens: maxResponseTokens ?? 400,
        selectedCompany: companyCode,
        selectedModel: model ?? 'gpt-4o-mini-2024-07-18',
        selectedInstructions: instructions,
        isVoice: currentAssistant.isVoice ?? false,
        voiceInitialMessage: currentAssistant.voiceInitialMessage,
        voiceInputNoiseReduction: currentAssistant.voiceInputNoiseReduction ?? '',
        voiceOutputSpeed: currentAssistant.voiceOutputSpeed ?? 1,
        voiceCreateResponse: currentAssistant.voiceCreateResponse ?? true,
        voiceIdleTimeout: currentAssistant.voiceIdleTimeout ?? 10000,
        voiceFinishTimeout: currentAssistant.voiceFinishTimeout ?? 15000,
        voiceSilenceDuration: currentAssistant.voiceSilenceDuration ?? 500,
        voiceThreshold: currentAssistant.voiceThreshold ?? 0.5,
        voice: currentAssistant.voice ?? VOICE.MARIN,
        transcriptionModel: currentAssistant.transcriptionModel ?? 'gpt-4o-mini-transcribe-2025-12-15',
        employeeTypes: [],
        selectedEmployeeTypes: !StringUtils.isEmpty(analysisEmployeeTypes) ? analysisEmployeeTypes.split(',') : [],
    });

    const {
        companies,
        models,
        isLive,
        notifyAIWhilePaused,
        selectedName,
        selectedTemperature,
        selectedCompany,
        selectedMaxTokens,
        selectedModel,
        selectedInstructions,
        employeeTypes,
        selectedEmployeeTypes,
        isVoice,
        voiceInitialMessage,
        voiceInputNoiseReduction,
        voiceOutputSpeed,
        voiceCreateResponse,
        voiceIdleTimeout,
        voiceFinishTimeout,
        voiceSilenceDuration,
        voiceThreshold,
        voice,
        transcriptionModel,
    } = state;

    const {
        data: usersData,
        loading: loadingUsersData,
        error: errorLoadingUsersData,
    } = useQuery(UserQuery.GET_USERS_BY_COMPANY, {
        variables: {
            search: state.globalSearch,
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const {
        data: enabledCompaniesData,
        loading: loadingEnabledCompanies,
        error: errorLoadingEnabledCompanies,
    } = useQuery(AIQuery.PULL_COMPANIES_AI_KEY_ADDED, {
        fetchPolicy: FetchPolicy.NO_CACHE,
        skip: !AI_GLOBAL_SETTINGS_READ,
    });

    const [loadModels, { loading: loadingModels }] = useLazyQuery(AIQuery.GET_MODELS, {
        onCompleted: (response) => {
            if (response) {
                const data = response.getModels;
                setState((prevState) => ({
                    ...prevState,
                    models: data,
                }));
            }
        },
        onError: (err) => {
            ModalUtils.errorMessage([err]);
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const [createAssistant, { loading: creatingAssistant }] = useMutation(AIMutation.CREATE_ASSISTANT, {
        onCompleted: () => {
            toggleDialog();
            ModalUtils.successMessage(null, 'Assistant created successfully');
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [editAssistant, { loading: editingAssistant }] = useMutation(AIMutation.MODIFY_ASSISTANT, {
        onCompleted: () => {
            toggleDialog();
            ModalUtils.successMessage(null, 'Assistant updated successfully');
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    useEffect(() => {
        if (selectedCompany != null && AI_GLOBAL_SETTINGS_READ) loadModels({ variables: { companyCode: selectedCompany } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany]);

    useEffect(() => {
        if (errorLoadingEnabledCompanies) {
            ModalUtils.errorMessage(errorLoadingEnabledCompanies?.graphQLErrors);
            return;
        }

        if (!loadingEnabledCompanies) {
            const data = enabledCompaniesData?.pullCompaniesAIKeyAdded;
            if (companies) {
                setState((prevState) => ({
                    ...prevState,
                    companies: (data ?? []).filter((i) => i.platform === AI_PLATFORM.OPEN_AI),
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingEnabledCompanies, errorLoadingEnabledCompanies]);

    useEffect(() => {
        if (errorLoadingUsersData) {
            ModalUtils.errorMessage(errorLoadingUsersData?.graphQLErrors);
            return;
        }

        if (!loadingUsersData) {
            const users = usersData?.getUsersByCompany;
            if (users) {
                setState((prevState) => ({
                    ...prevState,
                    employeeTypes: [
                        ...new Set(
                            users
                                .filter((user) => !StringUtils.isEmpty(user.employeeType) && !['Integration', 'Other'].includes(user.employeeType))
                                .map((user) => user.employeeType).sort((a, b) => a.localeCompare(b)),
                        ),
                    ],
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingUsersData, errorLoadingUsersData]);

    const onChange = (name, value) => {
        if (name === 'isVoice') {
            setState((prevState) => ({
                ...prevState,
                [name]: value,
                selectedTemperature: 0.8,
                selectedModel: value ? 'gpt-realtime-mini-2025-12-15' : 'gpt-4o-mini-2024-07-18',
            }));
            return;
        }

        setState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const saveAssistant = () => {
        const selectedFiles = filesReference?.current?.getFilesSelected() ?? [];
        const selectedFunctions = functionsReference?.current?.getFunctionsSelected() ?? [];

        if (isVoice && StringUtils.isEmpty(voiceInitialMessage)) {
            ModalUtils.errorMessage(null, 'Initial message required to save the voice settings');
            return;
        }

        if (aiAssistantId) {
            const companyName = companies.find((c) => c.companyCode === selectedCompany)?.companyName;
            const payload = {
                assistantId: aiAssistantId,
                companyCode: selectedCompany,
                companyName,
                name: selectedName,
                instructions: selectedInstructions,
                model: selectedModel,
                temperature: selectedTemperature,
                maxResponseTokens: selectedMaxTokens,
                isLive,
                notifyAIWhilePaused,
                isVoice,
                voiceInitialMessage,
                voiceInputNoiseReduction: [NOISE_REDUCTION.NEAR, NOISE_REDUCTION.FAR].includes(voiceInputNoiseReduction) ? voiceInputNoiseReduction : null,
                voiceOutputSpeed,
                voiceCreateResponse,
                voiceIdleTimeout,
                voiceFinishTimeout,
                voiceSilenceDuration,
                voiceThreshold,
                voice,
                transcriptionModel,
                ...(selectedEmployeeTypes?.length > 0 ? { analysisEmployeeTypes: selectedEmployeeTypes.join(',') } : {}),
                ...(selectedFiles.length > 0 ? {
                    files: selectedFiles,
                } : {}),
                ...(selectedFunctions.length > 0 ? {
                    functions: selectedFunctions,
                } : {}),
            };

            editAssistant({
                variables: payload,
            });
            return;
        }

        const companyName = companies.find((c) => c.companyCode === selectedCompany)?.companyName;
        const payload = {
            companyCode: selectedCompany,
            companyName,
            name: selectedName,
            instructions: selectedInstructions,
            model: selectedModel,
            temperature: selectedTemperature,
            maxResponseTokens: selectedMaxTokens,
            isLive,
            notifyAIWhilePaused,
            isVoice,
            voiceInitialMessage,
            voiceInputNoiseReduction: [NOISE_REDUCTION.NEAR, NOISE_REDUCTION.FAR].includes(voiceInputNoiseReduction) ? voiceInputNoiseReduction : null,
            voiceOutputSpeed,
            voiceCreateResponse,
            voiceIdleTimeout,
            voiceFinishTimeout,
            voiceSilenceDuration,
            voiceThreshold,
            voice,
            transcriptionModel,
            ...(selectedEmployeeTypes?.length > 0 ? { analysisEmployeeTypes: selectedEmployeeTypes.join(',') } : {}),
            ...(selectedFiles.length > 0 ? {
                files: selectedFiles,
            } : {}),
            ...(selectedFunctions.length > 0 ? {
                functions: selectedFunctions,
            } : {}),
        };

        createAssistant({
            variables: payload,
        });
    };

    const isSaveDisabled = creatingAssistant
    || editingAssistant
    || StringUtils.isEmpty(selectedName)
    || (AI_GLOBAL_SETTINGS_READ && StringUtils.isEmpty(selectedCompany))
    || StringUtils.isEmpty(selectedInstructions);
    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            disableBackdropClick
            disableEscapeKeyDown
            scroll="paper"
            onMouseDown={(e) => e.stopPropagation()}
            disableEnforceFocus
        >
            <DialogAppBar
                appBarClassName={classes.AppBar}
                title="Assistant"
                onClose={() => toggleDialog()}
                toolbarSize="md"
            />
            <DialogContent>
                <div id="main-container" className={classes.content}>
                    <div className={classes.aiGeneralSettings}>
                        <div>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.labels}>Name</Form.Label>
                                <Form.Control
                                    maxLength="255"
                                    className={clsx(classes.input, StringUtils.isEmpty(selectedName) ? 'invalid-field' : '')}
                                    type="text"
                                    name="selectedName"
                                    value={selectedName ?? ''}
                                    onChange={(e) => onChange(e.target.name, e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.labels}>Temperature</Form.Label>
                                <Slider
                                    className={classes.slider}
                                    aria-labelledby="discrete-slider"
                                    valueLabelDisplay="auto"
                                    color="secondary"
                                    marks
                                    value={selectedTemperature}
                                    step={0.10}
                                    min={isVoice ? 0.6 : 0}
                                    max={isVoice ? 1.2 : 2}
                                    onChange={(_, value) => onChange('selectedTemperature', value)}
                                />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.labels}>Max Response Tokens</Form.Label>
                                <Slider
                                    className={classes.slider}
                                    aria-labelledby="discrete-slider"
                                    valueLabelDisplay="auto"
                                    color="secondary"
                                    marks
                                    value={selectedMaxTokens}
                                    step={100}
                                    min={100}
                                    max={2000}
                                    onChange={(_, value) => onChange('selectedMaxTokens', value)}
                                />
                            </Form.Group>
                        </div>
                        <div>
                            {AI_GLOBAL_SETTINGS_READ && (
                                <>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>Company</Form.Label>
                                        <Select
                                            size="sm"
                                            loading={loadingEnabledCompanies}
                                            className={clsx(classes.input, StringUtils.isEmpty(selectedCompany) ? 'invalid-field' : '')}
                                            name="selectedCompany"
                                            onChange={(name, value) => onChange(name, value)}
                                            value={selectedCompany ?? ''}
                                            options={companies.map((c) => ({
                                                value: c.companyCode,
                                                label: `${c.companyCode} - ${c.companyName}`,
                                            }))}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>Model</Form.Label>
                                        <Select
                                            size="sm"
                                            loading={loadingModels}
                                            className={classes.input}
                                            name="selectedModel"
                                            onChange={(name, value) => onChange(name, value)}
                                            value={selectedModel ?? ''}
                                            options={models.filter((m) => !isVoice || (isVoice && m.includes('-realtime-'))).map((m) => ({
                                                value: m,
                                                label: m,
                                            }))}
                                        />
                                    </Form.Group>
                                </>
                            )}
                            {!isVoice && (
                                <Form.Group as={Col}>
                                    <Form.Label className={classes.labels}>Employee Types for Smart Analysis (optional)</Form.Label>
                                    <CoreSelect
                                        className={classes.select}
                                        multiple
                                        name="employeeTypes"
                                        value={selectedEmployeeTypes ?? []}
                                        onChange={(e) => onChange('selectedEmployeeTypes', e.target.value)}
                                        renderValue={(selected) => selected.join(', ')}
                                        MenuProps={MenuProps}
                                    >
                                        {employeeTypes.map((employeeType, index) => (
                                            <MenuItem key={index} value={employeeType}>
                                                <Checkbox checked={(selectedEmployeeTypes || []).includes(employeeType)} />
                                                <ListItemText primary={employeeType} />
                                            </MenuItem>
                                        ))}
                                    </CoreSelect>
                                </Form.Group>
                            )}
                        </div>
                    </div>
                    <div className={classes.voiceSection}>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.labels}>Voice Settings</Form.Label>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={isVoice}
                                        onClick={(e) => onChange('isVoice', e.target.checked)}
                                    />
                                )}
                                label="Voice Enabled"
                            />
                        </Form.Group>
                        {isVoice && (
                            <div className={classes.voiceSettings}>
                                <div>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>Initial Message</Form.Label>
                                        <Form.Control
                                            maxLength="2000"
                                            className={clsx(classes.input, StringUtils.isEmpty(voiceInitialMessage) ? 'invalid-field' : '')}
                                            type="text"
                                            name="voiceInitialMessage"
                                            value={voiceInitialMessage ?? ''}
                                            onChange={(e) => onChange(e.target.name, e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>Noise Reduction</Form.Label>
                                        <Select
                                            size="sm"
                                            loading={false}
                                            className={classes.input}
                                            name="voiceInputNoiseReduction"
                                            onChange={(name, value) => onChange(name, value)}
                                            value={voiceInputNoiseReduction ?? ''}
                                            options={[
                                                {
                                                    value: '',
                                                    label: 'OFF',
                                                },
                                                ...Object.keys(NOISE_REDUCTION).map((k) => ({
                                                    value: NOISE_REDUCTION[k],
                                                    label: k,
                                                })),
                                            ]}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>Voice</Form.Label>
                                        <Select
                                            size="sm"
                                            loading={false}
                                            className={classes.input}
                                            name="voice"
                                            onChange={(name, value) => onChange(name, value)}
                                            value={voice ?? ''}
                                            options={Object.values(VOICE).map((v) => ({
                                                value: v,
                                                label: v,
                                            }))}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>Transcription Model</Form.Label>
                                        <Select
                                            size="sm"
                                            loading={false}
                                            className={classes.input}
                                            name="transcriptionModel"
                                            onChange={(name, value) => onChange(name, value)}
                                            value={transcriptionModel ?? ''}
                                            options={models.filter((m) => m === 'whisper-1' || m.includes('-transcribe-')).map((m) => ({
                                                value: m,
                                                label: m,
                                            }))}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>
                                            <Tooltip
                                                arrow
                                                title="Whether or not to automatically generate a response when a VAD (Voice Activity Detection)
                                                stop event occurs."
                                                placement="top"
                                            >
                                                <span>Voice Create Response</span>
                                            </Tooltip>
                                        </Form.Label>
                                        <div>
                                            <FormControlLabel
                                                control={(
                                                    <Checkbox
                                                        checked={voiceCreateResponse}
                                                        onClick={(e) => onChange('voiceCreateResponse', e.target.checked)}
                                                    />
                                                )}
                                                label="Enabled"
                                            />
                                        </div>
                                    </Form.Group>
                                </div>
                                <div>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>
                                            <Tooltip arrow title="The speed of the voice output." placement="top">
                                                <span>Voice Output Speed</span>
                                            </Tooltip>
                                        </Form.Label>
                                        <Slider
                                            className={classes.slider}
                                            aria-labelledby="discrete-slider"
                                            valueLabelDisplay="auto"
                                            color="secondary"
                                            marks
                                            value={voiceOutputSpeed}
                                            step={0.05}
                                            min={0.25}
                                            max={1.50}
                                            onChange={(_, value) => onChange('voiceOutputSpeed', value)}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>
                                            <Tooltip
                                                arrow
                                                title="Timeout after which a model response will be triggered automatically. This is useful for situations
                                                in which a long pause from the caller is unexpected."
                                                placement="top"
                                            >
                                                <span>Voice Idle Timeout (milliseconds)</span>
                                            </Tooltip>
                                        </Form.Label>
                                        <Slider
                                            className={classes.slider}
                                            aria-labelledby="discrete-slider"
                                            valueLabelDisplay="auto"
                                            color="secondary"
                                            marks
                                            value={voiceIdleTimeout}
                                            step={1000}
                                            min={5000}
                                            max={50000}
                                            onChange={(_, value) => onChange('voiceIdleTimeout', value)}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>
                                            <Tooltip
                                                arrow
                                                title="How long to wait before a call is automatically ended due to inactivity. It will
                                                start after the Idle Timeout."
                                                placement="top"
                                            >
                                                <span>Voice Finish Timeout (milliseconds)</span>
                                            </Tooltip>
                                        </Form.Label>
                                        <Slider
                                            className={classes.slider}
                                            aria-labelledby="discrete-slider"
                                            valueLabelDisplay="auto"
                                            color="secondary"
                                            marks
                                            value={voiceFinishTimeout}
                                            step={1000}
                                            min={5000}
                                            max={50000}
                                            onChange={(_, value) => onChange('voiceFinishTimeout', value)}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>
                                            <Tooltip
                                                arrow
                                                title="Duration of silence to detect speech stop. With shorter values the model will respond more quickly,
                                                but may jump in on short pauses from the caller."
                                                placement="top"
                                            >
                                                <span>Voice Silence Duration (milliseconds)</span>
                                            </Tooltip>
                                        </Form.Label>
                                        <Slider
                                            className={classes.slider}
                                            aria-labelledby="discrete-slider"
                                            valueLabelDisplay="auto"
                                            color="secondary"
                                            marks
                                            value={voiceSilenceDuration}
                                            step={100}
                                            min={200}
                                            max={5000}
                                            onChange={(_, value) => onChange('voiceSilenceDuration', value)}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.labels}>
                                            <Tooltip
                                                arrow
                                                title="A higher threshold will require louder audio to activate the model, and thus might perform better
                                                in noisy environments."
                                                placement="top"
                                            >
                                                <span>Voice Threshold</span>
                                            </Tooltip>
                                        </Form.Label>
                                        <Slider
                                            className={classes.slider}
                                            aria-labelledby="discrete-slider"
                                            valueLabelDisplay="auto"
                                            color="secondary"
                                            marks
                                            value={voiceThreshold}
                                            step={0.05}
                                            min={0}
                                            max={1}
                                            onChange={(_, value) => onChange('voiceThreshold', value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={classes.aiInstructions}>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.labels}>Instructions</Form.Label>
                            <Form.Control
                                className={StringUtils.isEmpty(selectedInstructions) ? 'invalid-field' : ''}
                                as="textarea"
                                size="sm"
                                value={selectedInstructions ?? ''}
                                onChange={(e) => onChange('selectedInstructions', e.target.value)}
                            />
                        </Form.Group>
                    </div>
                    {isVoice && (<div className={classes.aiFilesSection} />)}
                    {!isVoice && (
                        <AssistantFiles
                            ref={filesReference}
                            files={files}
                            parentActionInProgress={creatingAssistant || editingAssistant}
                            className={classes.aiFilesSection}
                        />
                    )}
                    <AssistantFunctions
                        ref={functionsReference}
                        existingFunctions={assistantFunctions}
                        parentActionInProgress={creatingAssistant || editingAssistant}
                        className={classes.aiFunctionsSection}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Form.Group as={Col}>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={isLive}
                                onClick={(e) => onChange('isLive', e.target.checked)}
                            />
                        )}
                        label="Live"
                    />
                    {!isVoice && (
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={notifyAIWhilePaused}
                                    onClick={(e) => onChange('notifyAIWhilePaused', e.target.checked)}
                                />
                            )}
                            label="Let AI be aware of conversation while paused (collections)"
                        />
                    )}
                </Form.Group>
                <Button
                    disabled={isSaveDisabled}
                    className={classes.containedSecondaryInfo}
                    size="small"
                    onClick={saveAssistant}
                >
                    {creatingAssistant || editingAssistant ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AssistantForm.defaultProps = {
    assistant: null,
};

AssistantForm.propTypes = {
    toggleDialog: PropTypes.func.isRequired,
    assistant: PropTypes.object,
};

export default AssistantForm;
